import React, { useMemo, useState } from "react";
import { PlanRun } from "../types";
import {
  ArrowRight,
  Check,
  Sparkles,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { getPlanById, savePlan } from "../services/storageService";
import { RichTextViewer } from "./RichTextEditor";
import { calculatePlanDuration, formatDuration } from "../shared/scheduling";

interface SmartSuggestionsProps {
  run: PlanRun;
  onPlanUpdated: () => void;
  canEdit: boolean;
  getToken: () => Promise<string | null>;
}

interface Suggestion {
  taskId: string;
  taskName: string;
  plannedMinutes: number;
  actualMinutes: number;
  diffMinutes: number;
}

export const SmartSuggestions: React.FC<SmartSuggestionsProps> = ({
  run,
  onPlanUpdated,
  canEdit,
  getToken,
}) => {
  const [isApplying, setIsApplying] = useState<string | "all" | null>(null);
  const [appliedTasks, setAppliedTasks] = useState<Set<string>>(new Set());

  // Fetch current plan to see what's already applied
  React.useEffect(() => {
    const checkApplied = async () => {
      try {
        const token = await getToken();
        const latestPlan = await getPlanById(run.planId, token || undefined);
        const newlyApplied = new Set<string>();

        if (run.planSnapshot?.tasks && run.taskStates) {
          run.planSnapshot.tasks.forEach((task) => {
            const state = run.taskStates![task.id];
            if (
              state &&
              state.status === "COMPLETED" &&
              state.startTime &&
              state.endTime
            ) {
              const actualMins = Math.round(
                (state.endTime - state.startTime) / 60000,
              );
              const currentTask = latestPlan.tasks?.find(
                (t) => t.id === task.id,
              );
              if (currentTask && currentTask.durationMinutes === actualMins) {
                newlyApplied.add(task.id);
              }
            }
          });
        }
        setAppliedTasks(newlyApplied);
      } catch (e) {
        console.error("Failed to check applied suggestions:", e);
      }
    };
    checkApplied();
  }, [run.planId, run.planSnapshot, run.taskStates, getToken]);

  const suggestions = useMemo(() => {
    if (!run.planSnapshot?.tasks || !run.taskStates) return [];

    const list: Suggestion[] = [];
    run.planSnapshot.tasks.forEach((task) => {
      const state = run.taskStates![task.id];
      if (
        state &&
        state.status === "COMPLETED" &&
        state.startTime &&
        state.endTime
      ) {
        const actualMs = state.endTime - state.startTime;
        const actualMins = Math.round(actualMs / 60000);
        const plannedMins = task.durationMinutes || 0;

        if (actualMins !== plannedMins && actualMins > 0) {
          list.push({
            taskId: task.id,
            taskName: task.name,
            plannedMinutes: plannedMins,
            actualMinutes: actualMins,
            diffMinutes: actualMins - plannedMins,
          });
        }
      }
    });
    return list;
  }, [run]);

  const pendingSuggestions = useMemo(() => {
    return suggestions.filter((s) => !appliedTasks.has(s.taskId));
  }, [suggestions, appliedTasks]);

  const totalPlanned = useMemo(() => {
    return calculatePlanDuration(run.planSnapshot?.tasks || []);
  }, [run.planSnapshot?.tasks]);

  const totalActual = useMemo(() => {
    if (!run.startTime || !run.endTime) return 0;
    return Math.round((run.endTime - run.startTime) / 60000);
  }, [run]);

  const handleApplyIndividual = async (suggestion: Suggestion) => {
    if (!canEdit || !run.planSnapshot) return;
    setIsApplying(suggestion.taskId);
    try {
      const token = await getToken();
      const latestPlan = await getPlanById(run.planId, token || undefined);

      const updatedTasks = latestPlan.tasks?.map((task) => {
        if (task.id === suggestion.taskId) {
          return { ...task, durationMinutes: suggestion.actualMinutes };
        }
        return task;
      });

      await savePlan(
        { ...latestPlan, tasks: updatedTasks },
        token || undefined,
      );
      setAppliedTasks((prev) => {
        const next = new Set(prev);
        next.add(suggestion.taskId);
        return next;
      });
      onPlanUpdated();
    } catch (e) {
      alert("Failed to update plan: " + e);
    } finally {
      setIsApplying(null);
    }
  };

  const handleApplyAll = async () => {
    if (!canEdit || !run.planSnapshot || pendingSuggestions.length === 0)
      return;
    setIsApplying("all");
    try {
      const token = await getToken();
      const latestPlan = await getPlanById(run.planId, token || undefined);

      const updatedTasks = latestPlan.tasks?.map((task) => {
        const suggestion = pendingSuggestions.find((s) => s.taskId === task.id);
        if (suggestion) {
          return { ...task, durationMinutes: suggestion.actualMinutes };
        }
        return task;
      });

      await savePlan(
        { ...latestPlan, tasks: updatedTasks },
        token || undefined,
      );
      setAppliedTasks((prev) => {
        const next = new Set(prev);
        pendingSuggestions.forEach((s) => next.add(s.taskId));
        return next;
      });
      onPlanUpdated();
    } catch (e) {
      alert("Failed to update plan: " + e);
    } finally {
      setIsApplying(null);
    }
  };

  if (!run.planSnapshot) return null;

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border-2 border-indigo-100 dark:border-indigo-900/30 overflow-hidden shadow-sm mb-6">
      <div className="bg-indigo-50/50 dark:bg-indigo-900/10 px-6 py-4 border-b border-indigo-100 dark:border-indigo-900/30 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-900/50 rounded-lg text-indigo-600 dark:text-indigo-400">
            <Sparkles size={20} />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900 dark:text-white">
              Plan complete!
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {suggestions.length > 0
                ? "We've found some ways to improve your plan's accuracy based on this run."
                : "This run perfectly matched your plan's expectations. Great job!"}
            </p>
          </div>
        </div>
        {canEdit && pendingSuggestions.length > 0 && (
          <button
            onClick={handleApplyAll}
            disabled={isApplying !== null}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-nowrap text-white text-xs font-bold rounded-lg shadow-sm shadow-indigo-500/20 transition-all flex items-center gap-2"
          >
            {isApplying === "all" ? (
              "Applying..."
            ) : (
              <>
                Apply all ({pendingSuggestions.length}) <ArrowRight size={14} />
              </>
            )}
          </button>
        )}
        {suggestions.length > 0 && pendingSuggestions.length === 0 && (
          <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-nowrap text-sm font-bold animate-in fade-in zoom-in duration-300">
            <Check size={18} /> All applied
          </div>
        )}
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-800">
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
              Planned Duration
            </div>
            <div className="text-xl font-mono font-bold text-slate-700 dark:text-slate-300">
              {formatDuration(totalPlanned)}
            </div>
          </div>
          <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-800">
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
              Actual Duration
            </div>
            <div className="text-xl font-mono font-bold text-slate-900 dark:text-white">
              {formatDuration(totalActual)}
            </div>
          </div>
          <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-800">
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
              Efficiency
            </div>
            <div
              className={`text-xl font-mono font-bold flex items-center gap-2 ${
                totalActual <= totalPlanned
                  ? "text-green-600 dark:text-green-400"
                  : "text-amber-600 dark:text-amber-400"
              }`}
            >
              {totalActual <= totalPlanned ? (
                <TrendingDown size={20} />
              ) : (
                <TrendingUp size={20} />
              )}
              {formatDuration(Math.abs(totalPlanned - totalActual))} {" "}
              {totalActual <= totalPlanned ? "Ahead" : "Behind"}
            </div>
          </div>
        </div>

        {suggestions.length > 0 && (
          <div className="space-y-3 mb-8">
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">
              Suggested Adjustments
            </h4>
            <div className="divide-y divide-slate-100 dark:divide-slate-800 border border-slate-100 dark:border-slate-800 rounded-xl overflow-hidden">
              {suggestions.map((s) => {
                const isApplied = appliedTasks.has(s.taskId);
                return (
                  <div
                    key={s.taskId}
                    className={`flex items-center justify-between p-4 transition-colors ${isApplied ? "bg-slate-50/50 dark:bg-slate-800/20" : "bg-white dark:bg-slate-900/50"}`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`p-2 rounded-lg ${
                          isApplied
                            ? "bg-slate-100 dark:bg-slate-800 text-slate-400"
                            : s.diffMinutes < 0
                              ? "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400"
                              : "bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400"
                        }`}
                      >
                        {isApplied ? (
                          <Check size={18} />
                        ) : s.diffMinutes < 0 ? (
                          <TrendingDown size={18} />
                        ) : (
                          <TrendingUp size={18} />
                        )}
                      </div>
                      <div>
                        <div
                          className={`text-sm font-bold ${isApplied ? "text-slate-500 dark:text-slate-400" : "text-slate-900 dark:text-white"}`}
                        >
                          {s.taskName}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          Actual: {formatDuration(s.actualMinutes)} · Planned:{" "}
                          {formatDuration(s.plannedMinutes)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right hidden sm:block">
                        <div
                          className={`text-sm font-bold ${
                            isApplied
                              ? "text-slate-400"
                              : s.diffMinutes < 0
                                ? "text-green-600 dark:text-green-400"
                                : "text-amber-600 dark:text-amber-400"
                          }`}
                        >
                          {s.diffMinutes < 0 ? "-" : "+"}
                          {formatDuration(Math.abs(s.diffMinutes))}
                        </div>
                        <div className="text-[10px] font-medium text-slate-400">
                          {s.diffMinutes < 0 ? "Faster" : "Slower"}
                        </div>
                      </div>

                      {canEdit && (
                        <button
                          onClick={() => !isApplied && handleApplyIndividual(s)}
                          disabled={isApplying !== null || isApplied}
                          className={`px-3 py-1.5 border text-[10px] font-bold rounded-lg transition-all shadow-sm uppercase tracking-wide ${
                            isApplied
                              ? "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-green-600 dark:text-green-400 opacity-100"
                              : "bg-white dark:bg-slate-800 border-indigo-200 dark:border-indigo-800 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 disabled:opacity-50"
                          }`}
                        >
                          {isApplying === s.taskId
                            ? "Applying..."
                            : isApplied
                              ? "Applied"
                              : "Apply"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {Object.entries(run.taskStates || {}).some(
          ([_, state]) => state.notes
        ) && (
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">
              Execution Notes
            </h4>
            <div className="space-y-3">
              {Object.entries(run.taskStates || {}).map(([taskId, state]) => {
                if (!state.notes) return null;
                const task = run.planSnapshot?.tasks?.find((t) => t.id === taskId);
                return (
                  <div
                    key={taskId}
                    className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700"
                  >
                    <div className="text-sm font-bold text-slate-900 dark:text-white mb-2">
                      {task?.name || "Unknown Task"}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-300">
                      <RichTextViewer content={state.notes} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};
